import { evaluate } from '@addictivegroup/addictive-offline'

/**
 * Field rules in the browser (D-03, D-41): the same rules and the same condition language as
 * Core's FieldRulesEngine, evaluated on the record as the API writes it, so a form saved
 * offline is not refused on arrival for a reason nobody could see.
 *
 * The rule record matches CrmRecord::toRuleRecord: fields, `<ref>_uuid`, `owner_uuid`,
 * `custom_fields`, and `rel.<relation>` one level deep.
 */
export function ruleRecord(entity, form, meta, related = {}) {
  const record = { ...form, custom_fields: { ...(form?.custom_fields || {}) } }
  for (const field of meta.fields(entity)) {
    if (field.type === 'ref') {
      const value = form?.[field.input] ?? form?.[field.name]?.uuid ?? null
      record[field.input] = value
    }
  }
  record.owner_uuid = form?.owner?.uuid ?? null
  record.rel = { ...related }
  if (record.stage_uuid && !record.rel.stage) {
    const stage = meta.stage(record.stage_uuid)
    if (stage) record.rel.stage = { key: stage.key, kind: stage.kind, uuid: stage.uuid, probability_default: stage.probability_default, position: stage.position }
  }
  return record
}

/** "field:name" → "name", "cf:region" → "custom_fields.region" (as the server keys them). */
export function effectKey(target) {
  if (target.startsWith('cf:')) return `custom_fields.${target.slice(3)}`
  if (target.startsWith('field:')) return target.slice(6)
  return target
}

/** @returns {Record<string, {visible?: boolean, required?: boolean, readonly?: boolean, options?: string[], pattern?: {regex: string, message?: string}}>} */
export function fieldEffects(rules, record, original = null) {
  const effects = {}
  for (const rule of rules || []) {
    let matches = false
    try {
      matches = evaluate(rule.condition, record, { original })
    } catch {
      continue
    }
    if (!matches) continue
    const key = effectKey(rule.target)
    effects[key] = { ...(effects[key] || {}), ...(rule.effects || {}) }
  }
  return effects
}

const valueAt = (record, key) => (key.startsWith('custom_fields.') ? record?.custom_fields?.[key.slice(14)] : record?.[key] ?? record?.[`${key}_uuid`])

/**
 * Whether a value fits a field's pattern (D-339): the whole value matches the regular
 * expression, in Unicode — as Core's FieldRulesEngine::fits reads it. A pattern the browser
 * cannot read is left to the server.
 */
export function patternFits(regex, value) {
  try {
    return new RegExp(`^(?:${regex})$`, 'u').test(String(value))
  } catch {
    return true
  }
}

/**
 * Values given that do not fit their field's pattern, as {key: what the person is told}: only
 * values changed from `original` (the record as stored) — one stored before the rule stays.
 */
export function unmetPatterns(effects, form, original = null, fallback = '') {
  const out = {}
  for (const [key, effect] of Object.entries(effects)) {
    if (!effect.pattern?.regex || effect.visible === false) continue
    const value = valueAt(form, key)
    if (value === null || value === undefined || value === '' || typeof value === 'object') continue
    if (original && String(valueAt(original, key) ?? '') === String(value)) continue
    if (!patternFits(effect.pattern.regex, value)) out[key] = effect.pattern.message || fallback
  }
  return out
}

/** Required fields (by rules) that are still empty — what Core would answer with FIELD_RULES_UNMET. */
export function unmetRequirements(effects, form) {
  const missing = []
  for (const [key, effect] of Object.entries(effects)) {
    if (!effect.required || effect.visible === false) continue
    const value = key.startsWith('custom_fields.') ? form?.custom_fields?.[key.slice(14)] : form?.[key] ?? form?.[`${key}_uuid`]
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) missing.push(key)
  }
  return missing
}
